import { useCallback, useMemo, useState } from "react";
import FileDownload from "@mui/icons-material/FileDownload";
import {
  Box,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { KsFacilityBreakdown } from "generated-graphql/graphql";
import { safeParseInt } from "encamp-shared/src/tierii/utilities";
interface KansasSummaryProps {
  facilities: KsFacilityBreakdown[];
}

/** Format a whole-dollar fee as currency, e.g. 175 → "$175" */
const formatFee = (amount: number) => `$${amount.toLocaleString()}`;

/** Format weight in lbs with K/M/B abbreviation, rounded to whole numbers (no decimals). */
function formatLbsRounded(lbs: number): string {
  if (lbs === 0) return "0 lbs";
  if (lbs >= 1e9) return `${Math.round(lbs / 1e9)} B lbs`;
  if (lbs >= 1e6) return `${Math.round(lbs / 1e6)} M lbs`;
  if (lbs >= 1e3) return `${Math.round(lbs / 1e3)} K lbs`;
  return `${Math.round(lbs)} lbs`;
}

/**
 * Kansas state-specific summary component.
 * Displays a facility breakdown table matching the KDHE Fee Calculation Worksheet
 * with non-EHS and EHS chemical counts, weights, fees, and a recommended total.
 */
export function KansasSummary({ facilities }: KansasSummaryProps) {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const totals = useMemo(() => {
    const nonEhsChemicalCount = facilities.reduce(
      (sum, f) => sum + f.nonEhsChemicalCount,
      0
    );
    const nonEhsTotalLbs = facilities.reduce(
      (sum, f) => sum + f.nonEhsTotalLbs,
      0
    );
    const ehsChemicalCount = facilities.reduce(
      (sum, f) => sum + f.ehsChemicalCount,
      0
    );
    const ehsTotalLbs = facilities.reduce(
      (sum, f) => sum + f.ehsTotalLbs,
      0
    );
    const recommendedTotalFee = facilities.reduce(
      (sum, f) => sum + f.totalFee,
      0
    );
    return {
      nonEhsChemicalCount,
      nonEhsTotalLbs,
      ehsChemicalCount,
      ehsTotalLbs,
      recommendedTotalFee,
    };
  }, [facilities]);

  const paginatedFacilities = useMemo(
    () => facilities.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [facilities, page, rowsPerPage]
  );

  const handleExportCsv = useCallback(() => {
    const csvRows: string[] = [
      [
        "Facility ID",
        "Facility Name",
        "Non-EHS Count",
        "Non-EHS Weight (lbs)",
        "Non-EHS Fee",
        "EHS Count",
        "EHS Weight (lbs)",
        "EHS Fee",
        "Total Fee",
      ].join(","),
    ];

    for (const facility of facilities) {
      const escapedName = `"${(facility.facilityName ?? "").replace(/"/g, '""')}"`;
      csvRows.push(
        [
          facility.facilityId,
          escapedName,
          facility.nonEhsChemicalCount,
          facility.nonEhsTotalLbs,
          facility.nonEhsFee,
          facility.ehsChemicalCount,
          facility.ehsTotalLbs,
          facility.ehsFee,
          facility.totalFee,
        ].join(",")
      );
    }

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `kansas-fee-breakdown-${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [facilities]);

  if (facilities.length === 0) {
    return null;
  }

  const summaryStats = [
    { label: "Facilities", value: String(facilities.length) },
    {
      label: "Non-EHS Chemicals",
      value: String(totals.nonEhsChemicalCount),
    },
    {
      label: "Non-EHS Weight",
      value: formatLbsRounded(totals.nonEhsTotalLbs),
    },
    {
      label: "EHS Chemicals",
      value: String(totals.ehsChemicalCount),
    },
    {
      label: "EHS Weight",
      value: formatLbsRounded(totals.ehsTotalLbs),
    },
    {
      label: "Recommended Total Fee",
      value: formatFee(totals.recommendedTotalFee),
    },
  ];

  return (
    <Box sx={{ mt: 4, pt: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Typography variant="h6" fontWeight={600}>
          Kansas Fee Calculation Breakdown
        </Typography>
        <Tooltip title="Download fee breakdown as CSV">
          <IconButton onClick={handleExportCsv} size="small" color="primary">
            <FileDownload />
          </IconButton>
        </Tooltip>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {summaryStats.map(({ label, value }) => (
          <Grid key={label} size={{ xs: 6, md: 2 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 500, lineHeight: 1.2 }}
              >
                {label}
              </Typography>
              <Typography variant="h6" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                {value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <TableContainer>
        <Table size="small" sx={{ tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, width: "29%" }}>
                Facility
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, width: "11%" }}>
                Non-EHS<br />&nbsp;
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, width: "11%" }}>
                Non-EHS<br />Wt
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, width: "11%" }}>
                Non-EHS<br />Fee
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, width: "7%" }}>
                EHS<br />&nbsp;
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, width: "10%" }}>
                EHS<br />Wt
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, width: "7%" }}>
                EHS<br />Fee
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, width: "14%" }}>
                Total
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedFacilities.map((facility) => (
              <TableRow key={facility.facilityId}>
                <TableCell
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {facility.facilityName}
                </TableCell>
                <TableCell align="right">
                  {facility.nonEhsChemicalCount}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  {formatLbsRounded(facility.nonEhsTotalLbs)}
                </TableCell>
                <TableCell align="right">
                  {formatFee(facility.nonEhsFee)}
                </TableCell>
                <TableCell align="right">
                  {facility.ehsChemicalCount}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  {formatLbsRounded(facility.ehsTotalLbs)}
                </TableCell>
                <TableCell align="right">
                  {formatFee(facility.ehsFee)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  {formatFee(facility.totalFee)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell
                colSpan={7}
                sx={{ fontWeight: 700, fontSize: "0.875rem" }}
              >
                RECOMMENDED TOTAL
              </TableCell>
              <TableCell
                align="right"
                sx={{ fontWeight: 700, fontSize: "0.875rem" }}
              >
                {formatFee(totals.recommendedTotalFee)}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={facilities.length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(safeParseInt(e.target.value) ?? 25);
          setPage(0);
        }}
        rowsPerPageOptions={[25, 50, 100]}
      />
    </Box>
  );
}
